import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  forgotPasswordReset,
  resetAuthStatuses,
} from "../../store/slices/authSlice";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Pulling unauthenticated tracking states from your updated slice
  const { resetPasswordStatus, error } = useSelector((state) => state.auth);

  // Form Field States
  const [email, setEmail] = useState("");
  const [hiringDate, setHiringDate] = useState(""); // Captures the "YYYY-MM-DD" format natively
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Visibility toggles & local checks
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState("");

  // Clean form flash banners on component mount/unmount
  useEffect(() => {
    dispatch(resetAuthStatuses());
    return () => dispatch(resetAuthStatuses());
  }, [dispatch]);

  // Handle post-success redirect loop
  useEffect(() => {
    if (resetPasswordStatus === "succeeded") {
      setEmail("");
      setHiringDate("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        navigate("/login"); // Redirects locked-out users back to login page
      }, 2500);
    }
  }, [resetPasswordStatus, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (!email || !hiringDate || !newPassword || !confirmPassword) {
      setLocalError("All fields are required.");
      return;
    }

    if (newPassword.length < 6) {
      setLocalError("New password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setLocalError("Passwords do not match.");
      return;
    }

    // 🚀 Triggers unauthenticated slice signature: { email, hiringDate, newPassword }
    dispatch(forgotPasswordReset({ email, hiringDate, newPassword }));
  };

  return (
    <div className="auth-page">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4">
            <div className="glass-card shadow-lg p-4 p-sm-5">
              {/* BRANDING HEADER */}
              <div className="text-center mb-4">
                <div className="mx-auto shadow-lg d-flex align-items-center justify-content-center text-white fw-bold mb-3 auth-icon-badge">
                  <i className="bi bi-unlock-fill fs-150"></i>
                </div>
                <h2 className="fw-bolder m-0 auth-brand-title">
                  Reset<span className="text-blue-500">Password</span>
                </h2>
                <p className="text-muted small mt-2 fw-medium">
                  Verify your account using your corporate credentials to reset
                  your password
                </p>
              </div>

              {/* LOCAL VALIDATION ERRORS */}
              {localError && (
                <div className="alert alert-danger py-2 border-0 small mb-3 d-flex align-items-center rounded-3">
                  <i className="bi bi-exclamation-circle-fill me-2"></i>
                  {localError}
                </div>
              )}

              {/* BACKEND REJECTION ERRORS */}
              {error && !localError && (
                <div className="alert alert-danger py-2 border-0 small mb-3 d-flex align-items-center rounded-3">
                  <i className="bi bi-exclamation-circle-fill me-2"></i>
                  {error}
                </div>
              )}

              {/* SUCCESS CONFIRMATION STATE */}
              {resetPasswordStatus === "succeeded" && (
                <div className="alert alert-success py-2 border-0 small mb-3 d-flex align-items-center rounded-3">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  Password recovery completed! Redirecting to login...
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* WORK EMAIL */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-secondary">
                    Work Email Address
                  </label>
                  <input
                    type="email"
                    className="form-control form-control-lg login-input fs-090 rounded-10"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={resetPasswordStatus === "loading"}
                    required
                  />
                </div>

                {/* HIRING DATE */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-secondary">
                    Official Hiring Date
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-lg login-input ${hiringDate ? "text-navy" : "text-slate-500"}`}
                    value={hiringDate}
                    onChange={(e) => setHiringDate(e.target.value)}
                    disabled={resetPasswordStatus === "loading"}
                    required
                  />
                </div>

                {/* NEW PASSWORD */}
                <div className="mb-3">
                  <label className="form-label fw-bold small text-secondary">
                    New Password
                  </label>
                  <div className="position-relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="form-control form-control-lg login-input login-input--with-icon"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={resetPasswordStatus === "loading"}
                      required
                    />
                    <button
                      type="button"
                      className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent pe-3 text-muted"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      <i
                        className={`bi ${showNewPassword ? "bi-eye-slash" : "bi-eye"}`}
                      ></i>
                    </button>
                  </div>
                </div>

                {/* CONFIRM NEW PASSWORD */}
                <div className="mb-4">
                  <label className="form-label fw-bold small text-secondary">
                    Confirm New Password
                  </label>
                  <div className="position-relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="form-control form-control-lg login-input login-input--with-icon"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={resetPasswordStatus === "loading"}
                      required
                    />
                    <button
                      type="button"
                      className="position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent pe-3 text-muted"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      <i
                        className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`}
                      ></i>
                    </button>
                  </div>
                </div>

                {/* SUBMIT ACTIONS BUTTON */}
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg fw-bold shadow-sm btn-hover border-0 transition-all auth-submit"
                    disabled={resetPasswordStatus === "loading"}
                  >
                    {resetPasswordStatus === "loading" ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving New Password...
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-link small text-decoration-none fw-semibold mt-1 text-slate-500 fs-085"
                    onClick={() => navigate("/login")}
                    disabled={resetPasswordStatus === "loading"}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </div>

            <div className="text-center mt-4">
              <p className="text-white small opacity-75">
                &copy; 2026 EMI Solutions Philippines Inc.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
